import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const QuestionnaireModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  // Timer logic - same as Django implementation
  const shouldShowQuestionnaireModal = () => {
    const lastSeen = localStorage.getItem('questionnaireModalLastSeen');
    const now = new Date().getTime();
    
    console.log('🔍 QuestionnaireModal Debug:');
    console.log('Last seen:', lastSeen);
    console.log('Current time:', now);
    
    // Check if this is a new user (no questionnaire modal seen before)
    if (!lastSeen) {
      console.log('✅ First time user - showing modal');
      return true;
    }
    
    // Additional check: if user just completed onboarding, show modal
    const onboardingCompleted = localStorage.getItem('onboardingCompleted');
    if (onboardingCompleted === 'true') {
      console.log('✅ User just completed onboarding - showing modal');
      // Clear the onboarding flag so it doesn't show again
      localStorage.removeItem('onboardingCompleted');
      return true;
    }
    
    // Additional check: if user has never seen the modal before (backup check)
    const hasSeenModal = localStorage.getItem('hasSeenQuestionnaireModal');
    if (!hasSeenModal) {
      console.log('✅ User has never seen modal before - showing modal');
      return true;
    }
    
    const lastSeenTime = parseInt(lastSeen);
    const tenSecondsInMs = 10 * 1000; // 10 seconds for testing
    const oneWeekInMs = 7 * 24 * 60 * 60 * 1000; // 7 days
    const oneMonthInMs = 30 * 24 * 60 * 60 * 1000; // 30 days
    
    // Change this to 'test', 'week', or 'month' to control frequency
    const frequency = 'month';
    const timeLimit = frequency === 'test' ? tenSecondsInMs : 
                     frequency === 'week' ? oneWeekInMs : oneMonthInMs;
    
    console.log('Last seen time:', lastSeenTime);
    console.log('Time limit:', timeLimit);
    console.log('Time difference:', now - lastSeenTime);
    console.log('Should show:', (now - lastSeenTime) > timeLimit);
    
    // Show modal if enough time has passed
    return (now - lastSeenTime) > timeLimit;
  };

  useEffect(() => {
    console.log('🚀 QuestionnaireModal useEffect triggered');
    
    // Add a small delay to ensure the component is fully mounted
    const timer = setTimeout(() => {
      // Check if modal should be shown when component mounts
      const shouldShow = shouldShowQuestionnaireModal();
      console.log('Should show modal:', shouldShow);
      if (shouldShow) {
        console.log('✅ Setting modal to visible');
        setIsVisible(true);
      }
    }, 100); // Small delay to ensure proper timing
    
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    // Mark modal as seen when dismissed
    localStorage.setItem('questionnaireModalLastSeen', new Date().getTime().toString());
    localStorage.setItem('hasSeenQuestionnaireModal', 'true');
  };

  const handleFillPreferences = () => {
    handleClose();
    navigate('/full-questionnaire/');
  };

  console.log('🎯 Modal render - isVisible:', isVisible);
  if (!isVisible) {
    console.log('❌ Modal not visible, returning null');
    return null;
  }

  return (
    <div className="modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999
    }}>
      <div className="modal-content" style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
      }}>
        {/* Modal Header */}
        <div className="modal-header" style={{
          padding: '20px 24px 16px 24px',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h5 className="modal-title" style={{
            color: '#009933',
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <i className="fa-solid fa-clipboard-question"></i>
            Want Even Better Recommendations?
          </h5>
          <button 
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body" style={{
          padding: '20px 24px'
        }}>
          <p style={{
            fontSize: '16px',
            lineHeight: '1.5',
            color: '#1a202c',
            margin: '0 0 16px 0'
          }}>
            Fill out your preferences to get personalized movie and TV show recommendations tailored just for you!
          </p>
          <p style={{
            fontSize: '14px',
            color: '#666',
            margin: '0'
          }}>
            You can update your preferences at any time from the home page.
          </p>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer" style={{
          padding: '16px 24px 24px 24px',
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          gap: '12px',
          justifyContent: 'center'
        }}>
          <button 
            onClick={handleClose}
            style={{
              background: '#6c757d',
              border: '1px solid #6c757d',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0px)';
            }}
          >
            Maybe Later
          </button>
          <button 
            onClick={handleFillPreferences}
            style={{
              background: '#009933',
              border: '1px solid #009933',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0px)';
            }}
          >
            Fill Out Preferences
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireModal; 